import { useState, useEffect, useCallback } from 'react';
import type { PendingAddressData, AddressSubmission } from '../../domain/models/postOrderAddress.types';
import type { PostOrderAddressService } from '../../application/services/PostOrderAddressService';
import { useToast } from '@/shared/widgets/toast/useToast';

export const usePendingAddress = (
  service: PostOrderAddressService, 
  identifiers: { orderCode: string | null; orderToken: string | null }
) => {
  const [data, setData] = useState<PendingAddressData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  
  const { toast } = useToast();
  const { orderCode, orderToken } = identifiers;

  const fetchAddress = useCallback(async () => {
    if (!orderCode && !orderToken) {
      setError("No valid order identifier provided.");
      setIsLoading(false);
      return;
    }
    
    try {
      setIsLoading(true);
      const result = await service.fetchPendingAddress({ orderCode, orderToken });
      setData(result);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(errorMessage || "Failed to fetch address details.");
    } finally {
      setIsLoading(false);
    }
  }, [orderCode, orderToken, service]);

  useEffect(() => {
    fetchAddress();
  }, [fetchAddress]);

  const verifyPincode = async (pincode: string) => {
    try {
      return await service.verifyPincode(pincode);
    } catch (err) {
      console.error("Failed to fetch pincode details", err);
      return null;
    }
  };

  const submitAddress = async (submission: AddressSubmission): Promise<boolean> => {
    try {
      setIsSubmitting(true);
      await service.submitAddress(submission);
      
      toast({
        variant: 'success',
        title: 'Success',
        description: 'Address updated successfully.',
      });
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update address.';

      toast({
        variant: 'destructive',
        title: 'Error',
        description: message,
      });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return { data, isLoading, error, isSubmitting, verifyPincode, submitAddress };
};